import { Suspense } from 'react';
import { Field } from '@progress/kendo-react-form';
import { Loader } from '@progress/kendo-react-indicators';
import { DialogProps, DisputeDialog, DisputeEventResult, FieldInput, FileUpload, Label } from 'components';
import { DisputeEventTypes } from 'enums';
import { useFileUploadValidator, useUploadDocuments } from 'hooks';
import { useDisputeService } from 'services';
import { useDisputeDetails, useDisputeTracking } from 'state';
import styles from './RouteToDot.module.scss';
import { DisputeDocuments } from 'state/uploadedDisputeDocument';

/**
 * RouteToDot component allows the supplier to route a dispute back to the assigned internal user.
 * 
 * @param props - Additional properties passed to the Dialog
 * @returns Rendered component
 */
export const RouteToDot = (props: DialogProps) => {
    const { assigneeEmail, disputeNumber } = useDisputeDetails();
    const disputeService = useDisputeService();
    const filesValidator = useFileUploadValidator(1);

    const { setNewDocuments} = useUploadDocuments();
    const onSubmit = async (data: any) => {
        const { disputeNumber, files, user, ...rest } = data;

        if (!(files?.length > 0)) {
            return {
                error: 'Please provide additional documentation'
            } as DisputeEventResult;
        }

        const fileData = files?.map((file: any) => file.getRawFile()) ?? [];
        const result = await disputeService?.routeToDot(disputeNumber, {
            ...rest,
            eventTypeId: DisputeEventTypes.DisputeRoutedBack,
            assigneeEmail,
        }, fileData);

        if(result) {
            const uploadedDocs: DisputeDocuments = {
                disputeNumber: disputeNumber,
                documents: result.data
            };
            setNewDocuments(uploadedDocs)
        }
        else {
            setNewDocuments(null)
        }

        return {
            error: result ? undefined : 'Error routing dispute'
        } as DisputeEventResult;
    };

    return (
        <DisputeDialog
            initialValues={{ isPublic: false }}
            title="Upload Documentation"
            showIsPublic={false}
            showNoteLabel={false}
            submitLabel="Submit"
            onSubmit={onSubmit}
            refreshDispute={true}
            {...props}
        >
            <div className={styles.label}>Dot's Notes</div>
            <Suspense fallback={<Loader type="infinite-spinner" />}>
                <Note disputeNumber={disputeNumber} />
            </Suspense> 
            <Label className={styles.label2} required>Additional Documentation</Label>
            <Field
                component={FieldInput}
                child={FileUpload}
                label="Please provide any additional documentation."
                name="files"
                validator={filesValidator}
                
            />
            <Label className={styles.label2} required>Comments</Label>
            <p>Please provide any additional information about the disputed adjustments.</p>
        </DisputeDialog>
    );
};

type NoteProps = {
    disputeNumber: string
};

const Note = ({ disputeNumber }: NoteProps) => {
    const tracking = useDisputeTracking(disputeNumber);
    const { note } = tracking.find(({ eventTypeId }) => eventTypeId === DisputeEventTypes.DisputeRoutedToVendor) ?? {
        note: 'Additional documentation is needed.'
    };
    return <p className={styles.note}>{note}</p>;
};